// Delivery riders (F-DLV-N1): the links a rider's ticket opens on their phone,
// and the words for outcomes. Pure, so the phone-number handling is testable.

import type { Custody, DeliveryIssue, FailedOutcome } from '@/types/rider'

/**
 * Google Maps turn-by-turn to the drop. The pin wins when the order has one
 * (storefront orders); otherwise the typed address is searched. On Android
 * this URL opens the Maps app straight into navigation.
 */
export function navigateUrl(lat: number | null, lng: number | null, address: string | null): string | null {
  if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`
  }
  const a = (address ?? '').trim()
  if (!a) return null
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a)}&travelmode=driving`
}

/** Digits only, Pakistani local numbers turned international (03xx → 923xx). */
export function internationalDigits(phone: string | null | undefined): string | null {
  let d = (phone ?? '').replace(/[^\d+]/g, '')
  if (!d) return null
  if (d.startsWith('+')) d = d.slice(1)
  else if (d.startsWith('00')) d = d.slice(2)
  else if (d.startsWith('0')) d = '92' + d.slice(1)
  else if (d.length === 10 && d.startsWith('3')) d = '92' + d
  return d.length >= 10 ? d : null
}

export function telUrl(phone: string | null | undefined): string | null {
  const d = internationalDigits(phone)
  return d ? `tel:+${d}` : null
}

export function whatsAppUrl(phone: string | null | undefined, text?: string): string | null {
  const d = internationalDigits(phone)
  if (!d) return null
  return `https://wa.me/${d}${text ? `?text=${encodeURIComponent(text)}` : ''}`
}

export const FAILED_OUTCOME_LABEL: Record<FailedOutcome, string> = {
  customer_refused: 'Customer refused the order',
  customer_unreachable: 'Customer not reachable',
  wrong_address: 'Wrong or unclear address',
  other: 'Something else',
}

export const ISSUE_LABEL: Record<DeliveryIssue, string> = {
  late: 'Arrived late',
  damaged: 'Food damaged or spilled',
  missing_item: 'An item was missing',
  short_paid: 'Customer paid less',
  other: 'Something else',
}

export const CUSTODY_LABEL: Record<Custody, string> = {
  cash: 'Cash',
  rider_wallet: "Rider's wallet",
  business_wallet: "Restaurant's account",
  card_terminal: 'Card machine',
}

/** What is still owed on the order, never negative, paisa-rounded. */
export function amountDue(total: number, paid: number): number {
  return Math.max(0, Math.round((total - paid) * 100) / 100)
}
